import { z } from 'zod';
import { chatMessageSchema, reasoningEffortSchema } from './agents';
import type { ProviderDto } from './providers';
import { MAX_SESSION_LEVELS, sessionLevelSchema } from './sessions';

/**
 * The agent builder: a chat that turns a use case into changes to an agent's draft. The model
 * replies in prose and, when it has a proposal, ends with one fenced `agent-changes` JSON block.
 * Nothing is applied on the server; the console shows the proposal and applies it on request.
 */

export const AGENT_CHANGES_FENCE = 'agent-changes';

/** Strongest first: the builder writes prompts and plans tool use, so it wants a capable model. */
const PREFERRED_BUILDER_MODELS = [
  /opus-5-5/,
  /sonnet-5-5/,
  /opus-5/,
  /sonnet-5/,
  /opus/,
  /sonnet/,
  /gpt-5/,
  /gemini-.*pro/,
];

type BuilderProvider = Pick<ProviderDto, 'id' | 'type' | 'status' | 'models' | 'defaultModel'>;

export interface BuilderModelChoice {
  providerId: string;
  model: string;
  /** `local`: the Local Claude connection; else the strongest model found. */
  source: 'local' | 'automatic';
}

const strongest = (models: string[]) => {
  for (const pattern of PREFERRED_BUILDER_MODELS) {
    const m = models.find((x) => pattern.test(x));
    if (m) return m;
  }
  return undefined;
};

/**
 * The model the Agent Builder runs on unless the user picks another: the Local Claude connection
 * (Claude Code on the server) when it works, else the strongest model found.
 */
export function defaultBuilderModel(providers: BuilderProvider[]): BuilderModelChoice | null {
  const usable = providers.filter((p) => p.status === 'ok' && (p.models.length || p.defaultModel));
  const local = usable.find((p) => p.type === 'claude_agent_sdk');
  if (local) {
    const model = local.defaultModel ?? strongest(local.models) ?? local.models[0]!;
    return { providerId: local.id, model, source: 'local' };
  }
  for (const pattern of PREFERRED_BUILDER_MODELS) {
    for (const p of usable) {
      const m = p.models.find((x) => pattern.test(x));
      if (m) return { providerId: p.id, model: m, source: 'automatic' };
    }
  }
  const p = usable[0] ?? providers[0];
  const model = p?.defaultModel ?? p?.models[0];
  return p && model ? { providerId: p.id, model, source: 'automatic' } : null;
}

/** The ranges `sessionLevelSchema` accepts for each level's history and notes limits. */
const LEVEL_LIMITS = {
  history: { maxMessages: [1, 200], maxTokens: [100, 200_000] },
  notes: { maxChars: [100, 20_000] },
} as const;

/**
 * Models often write 0 for limits a level doesn't use (e.g. `history.maxMessages` on a level that
 * keeps no history). Rather than reject the whole proposal: limits of a turned-off part fall back
 * to the defaults, limits of a part in use are clamped into range.
 */
function fixLevelLimits(level: unknown): unknown {
  if (!level || typeof level !== 'object') return level;
  const fixed: Record<string, unknown> = { ...level };
  for (const [part, limits] of Object.entries(LEVEL_LIMITS)) {
    const raw = fixed[part];
    if (!raw || typeof raw !== 'object') continue;
    const block: Record<string, unknown> = { ...raw };
    for (const [field, [min, max]] of Object.entries(limits)) {
      const value = block[field];
      if (typeof value !== 'number' || (Number.isInteger(value) && value >= min && value <= max))
        continue;
      if (block.enabled === false || !Number.isFinite(value)) delete block[field];
      else block[field] = Math.min(max, Math.max(min, Math.round(value)));
    }
    fixed[part] = block;
  }
  return fixed;
}

export const agentBuilderRequestSchema = z.object({
  /** The connection and model the builder itself runs on (not the agent's own model). */
  providerConnectionId: z.uuid(),
  model: z.string().min(1).max(200),
  messages: z
    .array(chatMessageSchema)
    .min(1)
    .max(80)
    .refine((m) => m.at(-1)?.role === 'user', {
      message: 'The last message must be from the user',
    }),
});
export type AgentBuilderRequest = z.infer<typeof agentBuilderRequestSchema>;

const nameList = z.array(z.string().min(1).max(200)).max(100).default([]);
const attachmentChange = z.object({ add: nameList, remove: nameList });

/** A proposed change set. Every field is optional: only what should change is present. */
export const agentChangesSchema = z.object({
  summary: z.string().max(4000).default(''),
  /** The complete new system prompt (not a diff). */
  systemPrompt: z.string().max(100_000).optional(),
  /** The model connection the agent runs on, by its name. */
  provider: z.string().min(1).max(200).optional(),
  model: z.string().min(1).max(200).optional(),
  tools: attachmentChange.optional(),
  mcpServers: attachmentChange.optional(),
  skills: attachmentChange.optional(),
  workflows: attachmentChange.optional(),
  /** Claude Code built-in tools (Local Claude connections only; the server allowlists them). */
  claudeBuiltinTools: attachmentChange.optional(),
  memory: z
    .object({
      mode: z.enum(['stateless', 'stateful', 'sessions']),
      lastMessages: z.number().int().min(1).max(100).optional(),
      /** The complete list of session levels (memory mode `sessions`), broad → specific. */
      sessionLevels: z
        .array(z.preprocess(fixLevelLimits, sessionLevelSchema))
        .max(MAX_SESSION_LEVELS)
        .optional(),
    })
    .superRefine((memory, ctx) => {
      const names = (memory.sessionLevels ?? []).map((l) => l.name);
      names.forEach((name, i) => {
        if (names.indexOf(name) !== i)
          ctx.addIssue({
            code: 'custom',
            path: ['sessionLevels', i, 'name'],
            message: 'Names must be unique',
          });
      });
    })
    .optional(),
  limits: z
    .object({
      maxSteps: z.number().int().min(1).max(50).optional(),
      maxDurationSeconds: z.number().int().min(1).max(600).optional(),
      maxOutputTokens: z.number().int().min(1).max(200_000).optional(),
    })
    .optional(),
  params: z
    .object({
      temperature: z.number().min(0).max(2).optional(),
      topP: z.number().min(0).max(1).optional(),
      maxTokens: z.number().int().min(1).max(200_000).optional(),
      effort: reasoningEffortSchema.optional(),
    })
    .optional(),
});
export type AgentChanges = z.infer<typeof agentChangesSchema>;

export interface ExtractedChanges {
  /** The reply with the change block removed (what to show as text). */
  text: string;
  changes: AgentChanges | null;
  /** Why a change block that was present could not be used. */
  error: string | null;
  /** A change block has started but not finished yet (the reply is still streaming). */
  pending: boolean;
}

const FENCE = new RegExp('```' + AGENT_CHANGES_FENCE + '\\s*\\n([\\s\\S]*?)```', 'm');
/** An opening fence with no close yet: the block is still streaming in. */
const OPEN_FENCE = new RegExp('```' + AGENT_CHANGES_FENCE + '[\\s\\S]*$', 'm');

/** Splits a builder reply into its prose and its (validated) change set. */
export function extractAgentChanges(reply: string): ExtractedChanges {
  const match = FENCE.exec(reply);
  if (!match) {
    // While streaming, hide a half-written block instead of showing raw JSON.
    const text = reply.replace(OPEN_FENCE, '').trimEnd();
    return { text, changes: null, error: null, pending: text !== reply.trimEnd() };
  }
  const text = (reply.slice(0, match.index) + reply.slice(match.index + match[0].length)).trim();
  let json: unknown;
  try {
    json = JSON.parse(match[1]!);
  } catch {
    return {
      text,
      changes: null,
      error: 'The proposed changes were not valid JSON.',
      pending: false,
    };
  }
  const parsed = agentChangesSchema.safeParse(json);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    return {
      text,
      changes: null,
      error: `The proposed changes were not usable${issue ? ` (${issue.path.join('.') || 'root'}: ${issue.message})` : ''}.`,
      pending: false,
    };
  }
  return { text, changes: parsed.data, error: null, pending: false };
}
